import { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@Utils/cn';
import { FlowStagePill } from '../FlowStagePill';
import type { FlowStageInfo } from '../types';

export interface FlowStageProgressBarProps {
  stages: FlowStageInfo[];
  activeStageId: string | null;
  onStageSelect?: (stageId: string) => void;
  onPrev?: () => void;
  onNext?: () => void;
  disablePrev?: boolean;
  disableNext?: boolean;
  className?: string;
}

const GAP = 24;
const PADDING = 24;
const PREV_RATIO = 0.3;
const FOCUSED_RATIO = 0.5;
const NEXT_RATIO = 0.2;

function getCardWidth(pos: number, cardSpace: number): number {
  switch (pos) {
    case -1:
      return cardSpace * PREV_RATIO;
    case 0:
      return cardSpace * FOCUSED_RATIO;
    case 1:
      return cardSpace * NEXT_RATIO;
    default:
      return 0;
  }
}

function getMarginLeft(pos: number, activeIndex: number): number {
  const isVisible = pos >= -1 && pos <= 1;
  const isFirstVisible = pos === -1 || (pos === 0 && activeIndex === 0);
  return isVisible && !isFirstVisible ? GAP : 0;
}

export function FlowStageProgressBar({
  stages,
  activeStageId,
  onStageSelect,
  onPrev,
  onNext,
  disablePrev,
  disableNext,
  className,
}: FlowStageProgressBarProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.clientWidth);
      setReady(true);
    }
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const activeIndex = Math.max(
    0,
    stages.findIndex((s) => s.id === activeStageId),
  );
  const cardSpace = Math.max(0, containerWidth - PADDING * 2 - GAP * 2);

  const focusedTargetLeft = PADDING + cardSpace * PREV_RATIO + GAP;
  let focusedLeftInTrack = 0;
  for (let j = 0; j < activeIndex; j++) {
    const pos = j - activeIndex;
    focusedLeftInTrack +=
      getCardWidth(pos, cardSpace) + getMarginLeft(pos, activeIndex);
  }
  focusedLeftInTrack += getMarginLeft(0, activeIndex);
  const translateX = focusedTargetLeft - focusedLeftInTrack;

  return (
    <div
      ref={containerRef}
      className={cn('w-full shrink-0 pb-4', className)}
      style={{ overflowX: 'clip' }}
    >
      <div className="relative h-12 overflow-hidden">
        <div className="absolute inset-x-4 inset-y-0 rounded-full bg-zinc-900" />

        {onPrev && (
          <Button
            variant="outline"
            size="icon"
            onClick={onPrev}
            disabled={disablePrev}
            className="absolute top-1/2 left-8 z-10 h-10 w-10 -translate-y-1/2 rounded-md bg-zinc-800"
          >
            <ChevronLeft className="size-4" />
          </Button>
        )}

        {onNext && (
          <Button
            variant="outline"
            size="icon"
            onClick={onNext}
            disabled={disableNext}
            className="absolute top-1/2 right-8 z-10 h-10 w-10 -translate-y-1/2 rounded-md bg-zinc-800"
          >
            <ChevronRight className="size-4" />
          </Button>
        )}

        <div
          className={cn(
            'relative flex h-full items-center',
            ready && 'transition-transform duration-500 ease-in-out',
          )}
          style={{ transform: `translateX(${translateX}px)` }}
        >
          {stages.map((stage, i) => {
            const pos = i - activeIndex;
            const isVisible = pos >= -1 && pos <= 1;

            return (
              <div
                key={stage.id}
                className={cn(
                  'flex h-full shrink-0 items-center justify-center overflow-hidden',
                  ready && 'transition-all duration-500 ease-in-out',
                )}
                style={{
                  width: getCardWidth(pos, cardSpace),
                  marginLeft: getMarginLeft(pos, activeIndex),
                  opacity: isVisible ? 1 : 0,
                }}
              >
                <FlowStagePill
                  name={stage.name}
                  isActive={stage.id === activeStageId}
                  onClick={
                    onStageSelect ? () => onStageSelect(stage.id) : undefined
                  }
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
